import { useCallback, useMemo, useState } from "react";

import { ReservationRequestStatus } from "@/lib/gql/__generated__/graphql";

import * as Repository from "../repository";

import type * as Constants from "../constants";

type CInDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationRequestList, "cInDate">;
	from: string;
	to: string;
	reservationRequestStatus: ReservationRequestStatus;
};
type UpdatedDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationRequestList, "updatedDate">;
	from: string;
	to: string;
	reservationRequestStatus: ReservationRequestStatus;
};
type MonthCountForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationRequestList, "monthCount">;
	months: number;
	reservationRequestStatus: ReservationRequestStatus;
};

type Props = {
	hotelID: string;
};
export type SearchReservationRequestListProps =
	| CInDateForSearchCase
	| UpdatedDateForSearchCase
	| MonthCountForSearchCase;

type QueryVariables = { hotelID: string } & (CInDateForSearchCase | UpdatedDateForSearchCase | MonthCountForSearchCase);

type Result = {
	queryData: Repository.Query.QueryData.ReservationRequestList;
	// 予約の検索
	onSearchReservationRequestList: (props: SearchReservationRequestListProps) => void;
};

export const useSearchReservationRequestList = ({ hotelID }: Props): Result => {
	const [queryVariables, setQueryVariables] = useState<QueryVariables>({
		hotelID,
		type: "monthCount",
		months: 3,
		reservationRequestStatus: ReservationRequestStatus.Unnoticed,
	});
	/**
	 * チェックイン日での検索
	 */
	const { queryData: queryDataForSearchByCInDate } = Repository.Query.UseCase.useReservationRequestListByCInDateQuery(
		{ queryVariables: queryVariables?.type === "cInDate" ? queryVariables : undefined },
	);
	/**
	 * リクエスト日/更新日での検索
	 */
	const { queryData: queryDataForSearchByUpdatedDate } =
		Repository.Query.UseCase.useReservationRequestListByUpdatedDateQuery({
			queryVariables: queryVariables?.type === "updatedDate" ? queryVariables : undefined,
		});
	/**
	 * 直近3ヶ月の新着リクエストでの検索
	 */
	const { queryData: queryDataForSearchByMonthCountDate } =
		Repository.Query.UseCase.useReservationRequestListByMonthCountDateQuery({
			queryVariables: queryVariables?.type === "monthCount" ? queryVariables : undefined,
		});
	const queryData = useMemo(
		() =>
			queryDataForSearchByCInDate ??
			queryDataForSearchByUpdatedDate ??
			queryDataForSearchByMonthCountDate ?? {
				//
				hotelName: "",
				hotelPrefecture: "",
				reservationList: [],
			},
		[queryDataForSearchByCInDate, queryDataForSearchByMonthCountDate, queryDataForSearchByUpdatedDate],
	);

	/**
	 * 予約の検索
	 */
	const onSearchReservationRequestList = useCallback(
		(props: CInDateForSearchCase | UpdatedDateForSearchCase | MonthCountForSearchCase) => {
			setQueryVariables({ ...props, hotelID });
		},
		[hotelID],
	);

	return {
		queryData,
		// 予約の検索
		onSearchReservationRequestList,
	};
};
